import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const ease = [0.22, 1, 0.36, 1];

const CHAPTERS = [
    {
        number: "01",
        tag: "Diagnosis",
        title: "The Signal",
        zone: "#C5A059",
        zoneLabel: "RED · AMBER · GREEN",
        desc: "A one-to-one call where I run your raw numbers and read your financial traffic light. I tell you plainly which zone you're in — red, amber or green — and the exact moves to get to the next light.",
        points: [
            "Full income & spending breakdown",
            "Your hidden money leaks, exposed",
            "Red / Amber / Green verdict",
        ],
        image: "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    },
    {
        number: "02",
        tag: "Blueprint",
        title: "The Dream Plan",
        zone: "#C5A059",
        zoneLabel: "RELOCATE · BUY · BUILD",
        desc: "Want to move to Lisbon? Buy a £250k home? Go freelance? I build the math-backed roadmap for your goal. At your current pace I tell you the real date you'll hit it — and how to get there sooner.",
        points: [
            "A concrete roadmap for your goal",
            "The honest date you'll reach it",
            "Acceleration strategies that fit you",
        ],
        image: "https://images.unsplash.com/photo-1612367980327-7454a7276aa7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    },
    {
        number: "03",
        tag: "Business",
        title: "The Business Edge",
        zone: "#C5A059",
        zoneLabel: "FOR COMPANIES",
        desc: "Rigorous data analysis for businesses that want to cut waste and understand cash flow. The same raw honesty I bring to people, applied to your bottom line.",
        points: [
            "Cost optimisation analysis",
            "Cash-flow clarity",
            "Decision-ready reporting",
        ],
        image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    },
];

const Chapter = ({ ch, index }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);
    const reverse = index % 2 === 1;

    return (
        <div
            ref={ref}
            data-testid={`chapter-${ch.number}`}
            className="grid md:grid-cols-12 gap-8 md:gap-12 items-center py-20 md:py-28 border-t border-white/10"
        >
            <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1, ease }}
                className={`md:col-span-5 ${
                    reverse ? "md:order-2 md:col-start-8" : "md:order-1"
                }`}
            >
                <div className="relative overflow-hidden aspect-[4/5] bg-[#11141D]">
                    <motion.img
                        style={{ y }}
                        src={ch.image}
                        alt={ch.title}
                        className="absolute inset-0 w-full h-[120%] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                    <span
                        className="absolute top-5 left-5 overline text-white px-3 py-1.5 rounded-full backdrop-blur-md"
                        style={{ backgroundColor: `${ch.zone}cc` }}
                    >
                        {ch.zoneLabel}
                    </span>
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1, delay: 0.15, ease }}
                className={`md:col-span-6 ${
                    reverse ? "md:order-1 md:col-start-1" : "md:order-2 md:col-start-7"
                }`}
            >
                <div className="flex items-baseline gap-5 mb-6">
                    <span
                        className="font-display text-6xl md:text-7xl leading-none"
                        style={{ color: ch.zone }}
                    >
                        {ch.number}
                    </span>
                    <span className="overline text-white/40">/ {ch.tag}</span>
                </div>
                <h3 className="font-display text-4xl md:text-6xl tracking-tighter mb-6">
                    {ch.title}
                </h3>
                <p className="font-body text-lg text-[#8E95A3] leading-relaxed max-w-lg mb-8">
                    {ch.desc}
                </p>
                <ul className="flex flex-col gap-3">
                    {ch.points.map((p) => (
                        <li
                            key={p}
                            className="flex items-center gap-3 font-mono text-sm text-white/70"
                        >
                            <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: ch.zone }}
                            />
                            {p}
                        </li>
                    ))}
                </ul>
            </motion.div>
        </div>
    );
};

export const Manifesto = () => {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32"
        >
            <div className="grid md:grid-cols-12 gap-8 mb-8">
                <div className="md:col-span-8">
                    <p className="overline text-[#C5A059] mb-6">
                        — Three ways to take back control
                    </p>
                    <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-tight">
                        The services. No fluff,{" "}
                        <span className="italic text-[#C5A059]">
                            just the math
                        </span>
                        .
                    </h2>
                </div>
            </div>

            {CHAPTERS.map((ch, i) => (
                <Chapter key={ch.number} ch={ch} index={i} />
            ))}
        </section>
    );
};
